import { useState, useEffect, useCallback } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { Clock, ArrowUpRight, FolderOpen } from 'lucide-react';
import { getRecent } from '@/lib/recentFiles';
import { useFileDialog } from '@/hooks/useFileDialog';
import { openPdfForMode } from '@/lib/toolLaunch';

function formatRelativeTime(openedAt) {
  if (!openedAt) return '';
  const diff = Date.now() - openedAt;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

export default function RecentFilesView() {
  const navigate = useNavigate();
  const { openFile } = useFileDialog();
  const [recentFiles, setRecentFiles] = useState([]);
  const [openError, setOpenError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getRecent().then((list) => {
      if (!cancelled) setRecentFiles(Array.isArray(list) ? list : []);
    });
    return () => { cancelled = true; };
  }, []);

  const openRecent = useCallback(async (rf) => {
    if (!window.electronAPI?.readFile) return;
    setOpenError(null);
    try {
      const buffer = await window.electronAPI.readFile(rf.path);
      const file = new File([buffer], rf.name, { type: 'application/pdf' });
      navigate('/viewer', { state: { newDocument: true, file, filePath: rf.path } });
    } catch (err) {
      // Was a silent no-op — the user clicked and nothing happened. Say so.
      console.error('Failed to open recent file:', rf.path, err);
      setOpenError(rf.name);
    }
  }, [navigate]);

  const handleOpenFile = useCallback(async () => {
    const r = await openFile('.pdf');
    openPdfForMode(r, undefined, navigate);
  }, [openFile, navigate]);

  return (
    <div style={{ padding: '28px 32px 36px' }}>
      {/* Heading */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
          <Clock size={20} style={{ color: 'var(--ds-indigo, #4F46E5)' }} />
          <h2 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.3px', margin: 0 }}>
            Recent files
          </h2>
        </div>
        <p style={{ fontSize: 13.5, color: 'var(--ds-ink-3, #8C8FA0)', margin: 0 }}>
          Pick up where you left off. Files are stored locally on your device.
        </p>
      </div>

      {openError && (
        <div role="alert" style={{ fontSize: 13, color: 'var(--ds-danger, #DC2626)', marginBottom: 12 }}>
          Couldn’t open “{openError}”. It may have been moved, renamed, or deleted.
        </div>
      )}
      {recentFiles.length === 0 ? (
        /* Empty state */
        <div
          style={{
            border: '1.5px dashed var(--ds-line-3, #D2D3DD)',
            borderRadius: 14,
            padding: '52px 32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            background: 'var(--ds-surface-subtle, #FCFCFD)',
            maxWidth: 480,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: 'var(--ds-indigo-50, #EEF0FE)',
              color: 'var(--ds-indigo, #4F46E5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 16,
            }}
          >
            <FolderOpen size={24} strokeWidth={1.5} />
          </div>
          <div style={{ fontSize: 15.5, fontWeight: 600, letterSpacing: '-0.2px', marginBottom: 6 }}>
            No recent files yet
          </div>
          <p style={{ fontSize: 13, color: 'var(--ds-ink-3, #8C8FA0)', marginBottom: 20, lineHeight: 1.55 }}>
            Open a PDF to get started — it will appear here so you can pick up right where you left off.
          </p>
          <button
            type="button"
            onClick={handleOpenFile}
            style={{
              height: 38,
              padding: '0 20px',
              borderRadius: 9,
              background: 'linear-gradient(140deg, var(--ds-indigo, #4F46E5), var(--ds-indigo-strong, #4338CA))',
              color: 'var(--ds-content, #fff)',
              border: 'none',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Open a PDF
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, maxWidth: 640 }}>
          {recentFiles.map((rf, i) => (
            <button
              key={rf.path || rf.name || i}
              type="button"
              onClick={() => openRecent(rf)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '11px 14px',
                border: '1px solid var(--ds-line, #EAEBF0)',
                borderRadius: 11,
                background: 'var(--ds-content, #fff)',
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
                fontFamily: 'inherit',
              }}
            >
              {/* PDF thumbnail stub */}
              <div
                style={{
                  width: 36,
                  height: 44,
                  borderRadius: 6,
                  background: 'var(--ds-content, #fff)',
                  border: '1px solid var(--ds-line-2, #DEDFE7)',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'flex-start',
                  padding: '0 0 4px 4px',
                }}
              >
                <span
                  style={{
                    fontSize: 7,
                    fontWeight: 700,
                    color: '#fff',
                    background: 'var(--ds-coral, #EF6A5A)',
                    borderRadius: 3,
                    padding: '1px 3px',
                    letterSpacing: '0.3px',
                  }}
                >
                  PDF
                </span>
              </div>

              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 13.5,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: 'var(--ds-ink, #191A24)',
                  }}
                >
                  {rf.name}
                </div>
                {rf.openedAt && (
                  <div style={{ fontSize: 12, color: 'var(--ds-ink-3, #8C8FA0)', marginTop: 2 }}>
                    {formatRelativeTime(rf.openedAt)}
                  </div>
                )}
              </div>

              <ArrowUpRight size={16} style={{ color: 'var(--ds-ink-4, #A9ABB8)', flexShrink: 0 }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
